import { useTranslation } from "react-i18next"
import { Badge, Button, cn } from "@nexus/ui"
import type { PersonaOnboardingContinueSummary } from "../../types/personas.types"

export interface PersonasOnboardingSummaryProps {
  summary: PersonaOnboardingContinueSummary
  /** Personas still New after Finish — drives the no-decisions variant. */
  newCount?: number
  onGoToLibrary: () => void
  isPublishing?: boolean
}

/** Post-Finish celebration — animated summary before the library opens. */
export function PersonasOnboardingSummary({
  summary,
  newCount = 0,
  onGoToLibrary,
  isPublishing = false,
}: PersonasOnboardingSummaryProps) {
  const { t } = useTranslation()
  const hasNoDecisions =
    summary.brandPersonaCount === 0 && summary.industryPersonaCount === 0

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="personas-onboarding-summary">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {t("personas.title", "User Personas")}
          </h1>
          <Badge variant="status-delivered" dot={false} iconStart="plus">
            {t("personas.onboarding.summary.badge", "Setup complete")}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("personas.onboarding.summary.subtitle", "Setup complete.")}
        </p>
      </div>

      <section className="mx-auto flex w-full max-w-xl flex-col items-center px-2 py-6">
        <div
          className={cn(
            "flex w-full flex-col items-center gap-7 rounded-md border border-border bg-card px-6 py-10 text-center sm:px-10 sm:py-[60px]",
            "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-500 motion-safe:ease-out motion-reduce:animate-none",
          )}
        >
          {hasNoDecisions ? (
            <NoDecisionsBody newCount={newCount} />
          ) : (
            <CelebrationBody summary={summary} />
          )}

          <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
            {t(
              "personas.onboarding.summary.discoveryNote",
              "New personas will appear here automatically as the analysis engine discovers them.",
            )}
          </p>

          <div
            className={cn(
              "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 motion-safe:delay-300 motion-safe:fill-mode-both motion-reduce:animate-none",
            )}
          >
            <Button size="lg" onClick={onGoToLibrary} disabled={isPublishing}>
              {isPublishing
                ? t("personas.onboarding.summary.publishing", "Starting recalculation…")
                : t("personas.onboarding.summary.cta", "Go to my personas")}
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

function CelebrationBody({ summary }: { summary: PersonaOnboardingContinueSummary }) {
  const { t } = useTranslation()
  return (
    <>
      <div
        className={cn(
          "flex size-14 items-center justify-center rounded-full bg-[var(--success)] text-[var(--success-foreground)]",
          "motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-500 motion-safe:delay-100 motion-safe:fill-mode-both motion-reduce:animate-none",
        )}
        aria-hidden
      >
        <svg viewBox="0 0 24 24" className="size-7" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div
        className={cn(
          "flex max-w-md flex-col gap-2",
          "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 motion-safe:delay-150 motion-safe:fill-mode-both motion-reduce:animate-none",
        )}
      >
        <h2 className="text-[22px] font-semibold tracking-tight text-foreground">
          {t("personas.onboarding.summary.title", "Your persona set is ready")}
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t("personas.onboarding.summary.description", {
            brandCount: summary.brandPersonaCount,
            industryCount: summary.industryPersonaCount,
          })}
        </p>
      </div>

      <div
        className={cn(
          "grid w-full gap-3 sm:grid-cols-2",
          "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 motion-safe:delay-200 motion-safe:fill-mode-both motion-reduce:animate-none",
        )}
      >
        <StatTile
          value={`${summary.brandSpendCoveredPct}%`}
          label={t("personas.onboarding.summary.brandStat", {
            count: summary.brandPersonaCount,
          })}
          accent="brand"
        />
        <StatTile
          value={`${summary.marketCoveredPct}%`}
          label={t("personas.onboarding.summary.marketStat", {
            count: summary.industryPersonaCount,
          })}
          accent="market"
        />
      </div>
    </>
  )
}

function NoDecisionsBody({ newCount }: { newCount: number }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        "flex max-w-md flex-col gap-2",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 motion-safe:delay-150 motion-safe:fill-mode-both motion-reduce:animate-none",
      )}
      data-testid="personas-onboarding-summary-no-decisions"
    >
      <h2 className="text-[22px] font-semibold tracking-tight text-foreground">
        {t("personas.onboarding.summary.noDecisionsTitle", "Your library is ready to review")}
      </h2>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {t("personas.onboarding.summary.noDecisionsDescription", {
          defaultValue:
            "{{count}} personas are waiting in your library as New. You can review them anytime.",
          count: newCount,
        })}
      </p>
    </div>
  )
}

function StatTile({
  value,
  label,
  accent,
}: {
  value: string
  label: string
  accent: "brand" | "market"
}) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-md border border-border bg-muted px-4 py-4 text-center">
      <p
        className={cn(
          "text-3xl font-semibold tracking-tight tabular-nums",
          accent === "brand" ? "text-primary" : "text-[var(--competitor)]",
        )}
      >
        {value}
      </p>
      <p className="text-xs leading-relaxed text-muted-foreground">{label}</p>
    </div>
  )
}
